import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import TopBar from "../components/TopBar";
import { Button, Card, Input, Select, Textarea } from "../components/ui";
import { ArrowLeft, Vote } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const MATCH_TYPES = ["practice", "friendly", "tournament"];
const BALL_TYPES = ["leather", "tennis", "tape", "rubber"];

export default function PollNewPage() {
  const nav = useNavigate();
  const { id: pollId } = useParams();
  const { club } = useAuth();
  const canManage = ["owner", "admin", "manager"].includes(club?.my_role);
  const isEditMode = !!pollId;

  useEffect(() => {
    if (club && !canManage) {
      toast.error("Only club admins can create polls");
      nav("/polls", { replace: true });
    }
  }, [club, canManage, nav]);

  // Load poll data if editing
  useEffect(() => {
    if (isEditMode) {
      loadPoll();
    }
  }, [isEditMode, pollId]);

  const loadPoll = async () => {
    try {
      const { data } = await api.get(`/polls/${pollId}`);
      if (data.charges_posted) {
        toast.error("Cannot edit poll after charges have been posted");
        nav(`/polls/${pollId}`);
        return;
      }
      setForm({
        title: data.title || "",
        date: data.date ? data.date.split("T")[0] : new Date().toISOString().slice(0, 10),
        start_time: data.start_time || "07:00",
        end_time: data.end_time || "10:00",
        ground: data.ground || "",
        ground_location: data.ground_location || "",
        match_type: data.match_type || "friendly",
        ball_type: data.ball_type || "leather",
        notes: data.notes || "",
        max_players: data.max_players || 22,
        min_players: data.min_players || 12,
        waiting_limit: data.waiting_limit || 0,
        deadline: data.deadline || "",
        match_fee: data.match_fee || 0,
        sponsor_contribution: data.sponsor_contribution || 0,
        status: data.status || "published",
      });
    } catch (err) {
      toast.error(apiError(err));
      nav("/polls");
    }
  };

  const [form, setForm] = useState({
    title: "",
    date: new Date().toISOString().slice(0, 10),
    start_time: "07:00",
    end_time: "10:00",
    ground: "",
    ground_location: "",
    match_type: "friendly",
    ball_type: "leather",
    notes: "",
    max_players: 22,
    min_players: 12,
    waiting_limit: 0,
    deadline: "",
    match_fee: 0,
    sponsor_contribution: 0,
    status: "published",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e, status = "published") => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        start_time: form.start_time || null,
        end_time: form.end_time || null,
        max_players: Number(form.max_players),
        min_players: Number(form.min_players),
        waiting_limit: Number(form.waiting_limit) || 0,
        match_fee: Number(form.match_fee) || 0,
        sponsor_contribution: Number(form.sponsor_contribution) || 0,
        deadline: form.deadline || null,
        status,
      };
      let data;
      if (isEditMode) {
        // Update existing poll
        const { data: updatedData } = await api.put(`/polls/${pollId}`, payload);
        data = updatedData;
        toast.success("Poll updated successfully!");
      } else {
        // Create new poll
        const { data: newData } = await api.post("/polls", payload);
        data = newData;
        toast.success(status === "draft" ? "Draft saved" : "Poll published!");
      }
      nav(`/polls/${data.id}`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="pb-28">
      <div className="flex items-center px-3 pt-4">
        <button onClick={() => nav(-1)} className="h-10 w-10 rounded-full hover:bg-black/5 grid place-items-center" data-testid="back-btn">
          <ArrowLeft size={20}/>
        </button>
      </div>
      <TopBar title={isEditMode ? "Edit Match Poll" : "New Match Poll"} subtitle={isEditMode ? "Update poll details" : "Ask your team who's in"} />

      <form onSubmit={(e) => submit(e, "published")} className="px-5 space-y-4">
        <Card className="space-y-4">
          <Input label="Match Title *" placeholder="Sunday Practice" value={form.title} onChange={(e)=>setForm({...form,title:e.target.value})} required data-testid="poll-title" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Date *" type="date" value={form.date} onChange={(e)=>setForm({...form,date:e.target.value})} required data-testid="poll-date" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Start Time" type="time" value={form.start_time} onChange={(e)=>setForm({...form,start_time:e.target.value})} data-testid="poll-start-time" />
            <Input label="End Time" type="time" value={form.end_time} onChange={(e)=>setForm({...form,end_time:e.target.value})} data-testid="poll-end-time" />
          </div>
          <Input label="Ground *" placeholder="Crescent Ground" value={form.ground} onChange={(e)=>setForm({...form,ground:e.target.value})} required data-testid="poll-ground" />
          <Input label="Ground Location (Google Maps URL)" value={form.ground_location} onChange={(e)=>setForm({...form,ground_location:e.target.value})} placeholder="https://maps.google.com/…" data-testid="poll-location" />
          <div className="grid grid-cols-2 gap-3">
            <Select label="Match Type" value={form.match_type} onChange={(e)=>setForm({...form,match_type:e.target.value})} data-testid="poll-match-type">
              {MATCH_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
            </Select>
            <Select label="Ball Type" value={form.ball_type} onChange={(e)=>setForm({...form,ball_type:e.target.value})} data-testid="poll-ball-type">
              {BALL_TYPES.map(t => <option key={t} value={t} className="capitalize">{t}</option>)}
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input label="Min" type="number" min="2" value={form.min_players} onChange={(e)=>setForm({...form,min_players:e.target.value})} data-testid="poll-min-players" />
            <Input label="Max" type="number" min="2" value={form.max_players} onChange={(e)=>setForm({...form,max_players:e.target.value})} data-testid="poll-max-players" />
          </div>
          <Input label="Waiting Limit (0 for unlimited)" type="number" min="0" value={form.waiting_limit} onChange={(e)=>setForm({...form,waiting_limit:e.target.value})} data-testid="poll-waiting-limit" help="Players after this limit will join a waiting list" />
          <Input label="Registration Deadline" type="datetime-local" value={form.deadline} onChange={(e)=>setForm({...form,deadline:e.target.value})} data-testid="poll-deadline" />
          <div className="grid grid-cols-2 gap-3">
            <Input label="Match Fee ₹" type="number" min="0" value={form.match_fee} onChange={(e)=>setForm({...form,match_fee:e.target.value})} data-testid="poll-fee" />
            <Input label="Sponsor ₹" type="number" min="0" value={form.sponsor_contribution} onChange={(e)=>setForm({...form,sponsor_contribution:e.target.value})} data-testid="poll-sponsor" />
          </div>
          <Textarea label="Notes" value={form.notes} onChange={(e)=>setForm({...form,notes:e.target.value})} placeholder="Any details…" />
        </Card>

        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant="outline"
            size="lg"
            onClick={(e) => submit(e, "draft")}
            disabled={saving}
            data-testid="poll-save-draft"
          >
            Save draft
          </Button>
          <Button
            type="submit"
            size="lg"
            disabled={saving}
            data-testid="poll-publish"
          >
            <Vote size={16}/>
            {saving ? (isEditMode ? "Saving…" : "Publishing…") : (isEditMode ? "Save Changes" : "Publish poll")}
          </Button>
        </div>
      </form>
    </div>
  );
}
